import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  InputAdornment,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useUpdate, useNotification } from '@refinedev/core'
import { startOfDay, endOfDay } from 'date-fns'
import { enNZ } from 'date-fns/locale'
import { DiscountCode } from '@/interfaces/discountCodes'

interface EditDiscountCodeDialogProps {
  discountCode: DiscountCode
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

interface DiscountCodeFormData {
  code: string
  description: string
  discount_type: 'percentage'
  discount_value: number
  valid_from: Date | null
  valid_until: Date | null
  duration_months: number
  max_uses: number | null
  active: boolean
  archived: boolean
}

export default function EditDiscountCodeDialog({
  discountCode,
  open,
  onClose,
  onSuccess,
}: EditDiscountCodeDialogProps) {
  const { open: notify } = useNotification()
  const { mutate: updateDiscountCode, isLoading } = useUpdate()

  const [formData, setFormData] = useState<DiscountCodeFormData>({
    ...discountCode,
    valid_from: new Date(discountCode.valid_from),
    valid_until: new Date(discountCode.valid_until),
    active: discountCode.active,
    archived: discountCode.archived,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }

    if (!formData.valid_from) {
      newErrors.valid_from = 'Valid from date is required'
    }

    if (!formData.valid_until) {
      newErrors.valid_until = 'Valid until date is required'
    }

    if (
      formData.valid_from &&
      formData.valid_until &&
      formData.valid_from >= formData.valid_until
    ) {
      newErrors.valid_until = 'Valid until must be after valid from'
    }

    if (
      formData.max_uses !== null &&
      formData.max_uses < discountCode.current_uses
    ) {
      newErrors.max_uses = `Max uses must be greater than or equal to current uses (${discountCode.current_uses}) or empty for unlimited`
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const hasChanges = (): boolean => {
    return (
      formData.code !== discountCode.code ||
      formData.description !== discountCode.description ||
      formData.discount_type !== discountCode.discount_type ||
      formData.discount_value !== discountCode.discount_value ||
      formData.valid_from?.getTime() !==
        new Date(discountCode.valid_from)?.getTime() ||
      formData.valid_until?.getTime() !==
        new Date(discountCode.valid_until)?.getTime() ||
      formData.duration_months !== discountCode.duration_months ||
      formData.max_uses !== discountCode.max_uses ||
      formData.active !== discountCode.active ||
      formData.archived !== discountCode.archived
    )
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    try {
      await updateDiscountCode({
        resource: 'discount-codes',
        id: discountCode.id,
        values: {
          ...formData,
          valid_from: formData.valid_from
            ? startOfDay(formData.valid_from).toISOString()
            : null,
          valid_until: formData.valid_until
            ? endOfDay(formData.valid_until).toISOString()
            : null,
          max_uses: formData.max_uses || null,
        },
      })
      onSuccess()
      handleClose()
    } catch (error: any) {
      notify?.({
        type: 'error',
        message: error.message || 'Failed to update discount code',
      })
    }
  }

  const handleClose = () => {
    setErrors({})
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Discount Code</DialogTitle>
      <DialogContent>
        <FormControlLabel
          label="Active"
          control={
            <Checkbox
              checked={formData.active}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  active: e.target.checked,
                })
              }}
            />
          }
        />
        <FormControlLabel
          label="Archived"
          control={
            <Checkbox
              checked={formData.archived}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  archived: e.target.checked,
                })
              }}
            />
          }
        />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enNZ}>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {/* Code */}
              <Grid item xs={12}>
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    label="Discount Code"
                    value={formData.code}
                    disabled
                  />
                </Box>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  error={!!errors.description}
                  helperText={
                    errors.description ||
                    'Internal description for this discount code'
                  }
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Discount Value */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Discount Percentage"
                  disabled
                  type="number"
                  value={formData.discount_value}
                  error={!!errors.discount_value}
                  helperText={errors.discount_value}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Valid From and Until */}
              <Grid item xs={6}>
                <DatePicker
                  label="Valid From"
                  value={formData.valid_from}
                  onChange={(date) =>
                    setFormData({ ...formData, valid_from: date })
                  }
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.valid_from,
                      helperText: errors.valid_from,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <DatePicker
                  label="Valid Until"
                  value={formData.valid_until}
                  onChange={(date) =>
                    setFormData({ ...formData, valid_until: date })
                  }
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.valid_until,
                      helperText: errors.valid_until,
                    },
                  }}
                />
              </Grid>

              {/* Duration and Max Uses */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Discount Duration (months)"
                  type="number"
                  value={formData.duration_months}
                  disabled
                  error={!!errors.duration_months}
                  helperText={
                    errors.duration_months ||
                    'How long the discount applies to the subscription'
                  }
                  InputProps={{
                    inputProps: { min: 1 },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Max Uses (optional)"
                  type="number"
                  value={formData.max_uses || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_uses: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                  error={!!errors.max_uses}
                  helperText={
                    errors.max_uses || 'Leave empty for unlimited uses'
                  }
                  InputProps={{
                    inputProps: { min: 1 },
                  }}
                />
              </Grid>

              {/* Preview */}
              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Preview:</strong> Code "{formData.code || '[CODE]'}"
                  will give {formData.discount_value}% off for{' '}
                  {formData.duration_months} month
                  {formData.duration_months !== 1 ? 's' : ''}.{' '}
                  {formData.max_uses
                    ? `Limited to ${formData.max_uses} uses.`
                    : 'Unlimited uses.'}
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !hasChanges()}
        >
          {isLoading ? 'Updating...' : 'Update Discount Code'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
